/**
 * A word-level diff between two versions of a prose field.
 *
 * The edit history's job is to show what a reviewer changed, and a reviewer
 * mostly changes a few words inside a paragraph the agent otherwise got right.
 * Showing the before and after as two whole blocks makes the reader diff them
 * by eye, which is the work this is supposed to do for them.
 *
 * It is a WORD diff rather than a line diff because these fields are prose with
 * no line structure — a single unwrapped paragraph is one line, so a line diff
 * degenerates to "the whole thing changed" on every edit. And it renders inline
 * rather than side by side because the inspector rail is a narrow column; two
 * columns there would wrap every few words.
 */

export enum DiffKind {
  SAME = 'same',
  REMOVED = 'removed',
  ADDED = 'added',
}

export interface DiffSegment {
  kind: DiffKind;
  text: string;
}

/**
 * The most tokens either side may bring to the LCS pass.
 *
 * The table is quadratic, so an unbounded `fullProjectContext` — the longest
 * field on the draft — could stall the tab on open. Past the cap the change is
 * reported as a wholesale replacement, which is both cheaper and closer to the
 * truth: a diff that large is not something anyone reads word by word.
 *
 * The cap applies to what is LEFT after the common prefix and suffix are
 * trimmed, so the ordinary case — a long paragraph with one sentence rewritten
 * — stays a real diff no matter how long the paragraph is.
 */
const MAX_DIFF_TOKENS = 1200;

/**
 * Words carrying their own trailing whitespace, so joining the tokens back
 * reproduces the input exactly and no segment can lose a space at its edge.
 */
function tokenize(text: string): string[] {
  return text.match(/\S+\s*/g) ?? [];
}

function countCommonPrefix(before: string[], after: string[]): number {
  const shortest = Math.min(before.length, after.length);
  let count = 0;
  while (count < shortest && before[count] === after[count]) count++;
  return count;
}

function countCommonSuffix(
  before: string[],
  after: string[],
  alreadyMatched: number
): number {
  const shortest = Math.min(before.length, after.length) - alreadyMatched;
  let count = 0;
  while (
    count < shortest &&
    before[before.length - 1 - count] === after[after.length - 1 - count]
  ) {
    count++;
  }
  return count;
}

/**
 * The classic longest-common-subsequence walk, kept as tokens rather than
 * characters so the highlight lands on whole words.
 */
function diffMiddle(before: string[], after: string[]): DiffSegment[] {
  const rowCount = before.length;
  const columnCount = after.length;
  const stride = columnCount + 1;

  // Flat rather than nested: one allocation, and `Uint16Array` is safe because
  // MAX_DIFF_TOKENS keeps every subsequence length well inside 16 bits.
  const lengths = new Uint16Array((rowCount + 1) * stride);
  const lengthAt = (row: number, column: number): number =>
    lengths[row * stride + column] ?? 0;

  for (let row = rowCount - 1; row >= 0; row--) {
    for (let column = columnCount - 1; column >= 0; column--) {
      lengths[row * stride + column] =
        before[row] === after[column]
          ? lengthAt(row + 1, column + 1) + 1
          : Math.max(lengthAt(row + 1, column), lengthAt(row, column + 1));
    }
  }

  const segments: DiffSegment[] = [];
  let row = 0;
  let column = 0;

  while (row < rowCount && column < columnCount) {
    if (before[row] === after[column]) {
      segments.push({ kind: DiffKind.SAME, text: before[row] ?? '' });
      row++;
      column++;
    } else if (lengthAt(row + 1, column) >= lengthAt(row, column + 1)) {
      segments.push({ kind: DiffKind.REMOVED, text: before[row] ?? '' });
      row++;
    } else {
      segments.push({ kind: DiffKind.ADDED, text: after[column] ?? '' });
      column++;
    }
  }

  while (row < rowCount) {
    segments.push({ kind: DiffKind.REMOVED, text: before[row] ?? '' });
    row++;
  }
  while (column < columnCount) {
    segments.push({ kind: DiffKind.ADDED, text: after[column] ?? '' });
    column++;
  }

  return segments;
}

/** Adjacent runs of one kind become one segment, so the markup stays readable. */
function coalesce(segments: DiffSegment[]): DiffSegment[] {
  const merged: DiffSegment[] = [];

  for (const segment of segments) {
    if (segment.text === '') continue;

    const previous = merged[merged.length - 1];
    if (previous !== undefined && previous.kind === segment.kind) {
      previous.text += segment.text;
    } else {
      merged.push({ ...segment });
    }
  }

  return merged;
}

/**
 * The change from `before` to `after` as a run of segments to render in order.
 *
 * Identical inputs yield a single SAME segment rather than an empty list, so a
 * caller never has to special-case "nothing moved" to render something.
 */
export function diffWords(before: string, after: string): DiffSegment[] {
  const beforeTokens = tokenize(before);
  const afterTokens = tokenize(after);

  const prefixLength = countCommonPrefix(beforeTokens, afterTokens);
  const suffixLength = countCommonSuffix(
    beforeTokens,
    afterTokens,
    prefixLength
  );

  const beforeMiddle = beforeTokens.slice(
    prefixLength,
    beforeTokens.length - suffixLength
  );
  const afterMiddle = afterTokens.slice(
    prefixLength,
    afterTokens.length - suffixLength
  );

  const isTooLong =
    beforeMiddle.length > MAX_DIFF_TOKENS ||
    afterMiddle.length > MAX_DIFF_TOKENS;

  const middleSegments = isTooLong
    ? [
        { kind: DiffKind.REMOVED, text: beforeMiddle.join('') },
        { kind: DiffKind.ADDED, text: afterMiddle.join('') },
      ]
    : diffMiddle(beforeMiddle, afterMiddle);

  return coalesce([
    { kind: DiffKind.SAME, text: beforeTokens.slice(0, prefixLength).join('') },
    ...middleSegments,
    {
      kind: DiffKind.SAME,
      text: beforeTokens.slice(beforeTokens.length - suffixLength).join(''),
    },
  ]);
}
